import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function AddLead() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    source: "",
    stage: "new",
    lead_type: "buyer",
    property_interest: "",
    budget_min: "",
    budget_max: "",
    target_areas: "",
    pre_approved: false,
    sms_consent: false,
    email_consent: false,
    consent_source: "",
    notes: "",
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f };
      // Compose full_name on the client too, so a mobile agent who
      // switches between the split-name fields and the API sees the same
      // display value the backend will store.
      payload.full_name = [payload.first_name, payload.last_name].map(s => (s || "").trim()).filter(Boolean).join(" ");
      if (!payload.email) delete payload.email;
      ["budget_min", "budget_max"].forEach(k => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      const { data } = await api.post("/leads", payload);
      toast.success("Lead added");
      nav(`/leads/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand";
  const labelCls = "text-xs font-medium text-slate-600 uppercase tracking-wider";

  return (
    <div className="max-w-2xl">
      <Link to="/leads" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-leads">
        <ArrowLeft className="h-4 w-4" /> Back to leads
      </Link>
      <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Add a new lead</h1>
      <p className="mt-1 text-sm text-slate-500">Consent captured here determines what channels the AI is allowed to draft.</p>

      <form onSubmit={submit} className="mt-8 space-y-6 rounded-lg border border-slate-200 bg-white p-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>First name *</label>
            <input required value={f.first_name} onChange={e => set("first_name", e.target.value)} className={inputCls} data-testid="lead-first-name-input" />
          </div>
          <div>
            <label className={labelCls}>Last name *</label>
            <input required value={f.last_name} onChange={e => set("last_name", e.target.value)} className={inputCls} data-testid="lead-last-name-input" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={f.email} onChange={e => set("email", e.target.value)} className={inputCls} data-testid="lead-email-input" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input value={f.phone} onChange={e => set("phone", e.target.value)} className={inputCls} data-testid="lead-phone-input" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Address</label>
            <input value={f.address} onChange={e => set("address", e.target.value)} className={inputCls} placeholder="Home / mailing address" data-testid="lead-address-input" />
          </div>
          <div>
            <label className={labelCls}>Source</label>
            <input value={f.source} onChange={e => set("source", e.target.value)} className={inputCls} placeholder="Zillow, referral, open house…" data-testid="lead-source-input" />
          </div>
          <div>
            <label className={labelCls}>Stage</label>
            <select value={f.stage} onChange={e => set("stage", e.target.value)} className={inputCls} data-testid="lead-stage-select">
              {["new","contacted","nurturing","active","offer","under_contract"].map(s => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <select value={f.lead_type} onChange={e => set("lead_type", e.target.value)} className={inputCls} data-testid="lead-type-select">
              <option value="buyer">Buyer</option><option value="seller">Seller</option><option value="both">Both</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Price min</label>
            <input type="number" value={f.budget_min} onChange={e => set("budget_min", e.target.value)} className={inputCls} data-testid="lead-budget-min-input" />
          </div>
          <div>
            <label className={labelCls}>Price max</label>
            <input type="number" value={f.budget_max} onChange={e => set("budget_max", e.target.value)} className={inputCls} data-testid="lead-budget-max-input" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Property interest</label>
            <textarea value={f.property_interest} onChange={e => set("property_interest", e.target.value)} rows={2} className={inputCls} placeholder="Luxury single-family in Arcadia; pool required" data-testid="lead-property-interest-input" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Target areas</label>
            <input value={f.target_areas} onChange={e => set("target_areas", e.target.value)} className={inputCls} placeholder="Scottsdale, Arcadia…" />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input id="pre_approved" type="checkbox" checked={f.pre_approved} onChange={e => set("pre_approved", e.target.checked)} data-testid="lead-preapproved-checkbox" />
            <label htmlFor="pre_approved" className="text-sm text-slate-700">Pre-approved for financing</label>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Notes</label>
            <textarea value={f.notes} onChange={e => set("notes", e.target.value)} rows={3} className={inputCls} data-testid="lead-notes-input" />
          </div>
        </div>

        {/* Consent — legally weighty */}
        <div className="consent-callout rounded-md p-5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 mt-0.5" style={{ color: "var(--primary-hex)" }} />
            <div className="flex-1">
              <div className="font-heading font-bold text-sm tracking-tight text-slate-900">Contact consent — TCPA / CAN-SPAM</div>
              <p className="text-xs text-slate-600 mt-1">These flags gate automated outreach. A manual phone dial is not automated outreach and needs no consent. STOP or UNSUBSCRIBE on any channel sets Do Not Contact immediately.</p>

              <label className="mt-4 flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={f.sms_consent} onChange={e => set("sms_consent", e.target.checked)} className="mt-0.5" data-testid="lead-sms-consent" />
                <span className="text-sm text-slate-800"><strong>SMS consent.</strong> This lead has given express written consent to receive automated text messages at the phone number above (TCPA).</span>
              </label>
              <label className="mt-3 flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={f.email_consent} onChange={e => set("email_consent", e.target.checked)} className="mt-0.5" data-testid="lead-email-consent" />
                <span className="text-sm text-slate-800"><strong>Email consent.</strong> This lead has agreed to receive marketing email at the address above (CAN-SPAM).</span>
              </label>

              {(f.sms_consent || f.email_consent) && (
                <div className="mt-4">
                  <label className={labelCls}>How was consent obtained? (audit trail)</label>
                  <input value={f.consent_source} onChange={e => set("consent_source", e.target.value)} className={inputCls} placeholder="Open house sign-in card, website form, verbal on call…" data-testid="lead-consent-source" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Link to="/leads" className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</Link>
          <button type="submit" disabled={busy} className="rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: "var(--primary-hex)" }} data-testid="lead-save-btn">
            {busy ? "Saving…" : "Save lead"}
          </button>
        </div>
      </form>
    </div>
  );
}
